import { useProductInformationQuery } from "@src/components/plan/queries/product-information-query";
import { planQueryKeys } from "@src/components/plan/queries/query-keys";
import {
	type RedeemRewardMutationResult,
	useRedeemReward,
} from "@src/components/referral/mutations/redeem-reward";
import { referralProfileQueryKeys } from "@src/components/referral/queries/get-my-referral-profile";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import {
	clearReferralCheckoutTimestamp,
	ensureReferralCheckoutCooldownTimestampIfAbsent,
	setReferralCheckoutTimestampNow,
	setReferralWelcomeShown,
} from "@src/utils/referral-welcome-storage";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	REFERRAL_WELCOME_POPUP_OPEN_DELAY_MS,
	getRefereeSignUpReward,
	useReferralWelcomePopup,
} from "./use-referral-welcome-popup";

type ReferralWelcomeController = {
	opened: boolean;
	checkoutModalOpened: boolean;
	isRedeeming: boolean;
	proMonthly: string | null;
	rewardCode: string | undefined;
	referrerName: string;
	handleClose: () => void;
	handleClaimReward: () => Promise<void>;
	handleCheckoutClose: () => Promise<void>;
};

export function useReferralWelcomeController(): ReferralWelcomeController {
	const [opened, setOpened] = useState(false);
	const [checkoutModalOpened, setCheckoutModalOpened] = useState(false);
	const authenticatedUser = useAuthenticatedUser();
	const { shouldShow, referredByEmail, rewards } = useReferralWelcomePopup(
		authenticatedUser.id,
	);
	const { data: productInfo } = useProductInformationQuery();
	const { proMonthly } = productInfo.priceIds;
	const queryClient = useQueryClient();

	const signUpReward = useMemo(
		() => getRefereeSignUpReward(authenticatedUser.id, rewards),
		[authenticatedUser.id, rewards],
	);
	const rewardCode = signUpReward?.rewardCode;
	const canRender = !!proMonthly && !!rewardCode;

	useEffect(() => {
		if (!shouldShow || !canRender) return;

		const timer = setTimeout(() => {
			setReferralWelcomeShown();
			setOpened(true);
		}, REFERRAL_WELCOME_POPUP_OPEN_DELAY_MS);
		return () => clearTimeout(timer);
	}, [canRender, shouldShow]);

	const invalidateReferralAndPlan = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: referralProfileQueryKeys.all,
		});
		await queryClient.invalidateQueries({
			queryKey: planQueryKeys.myPlan(),
		});
	}, [queryClient]);

	const handleClose = useCallback(() => {
		setOpened(false);
		setReferralWelcomeShown();
		clearReferralCheckoutTimestamp();
	}, []);

	const { mutateAsync: claimReward, isPending: isRedeeming } = useRedeemReward(
		useCallback(async (result: RedeemRewardMutationResult) => {
			if (!result) return;

			if (result.needPaymentMethod) {
				setReferralCheckoutTimestampNow();
				setCheckoutModalOpened(true);
				return;
			}

			setReferralWelcomeShown();
			clearReferralCheckoutTimestamp();
			setOpened(false);
		}, []),
	);

	const handleClaimReward = useCallback(async () => {
		if (!rewardCode) return;
		await claimReward({ rewardCode });
	}, [claimReward, rewardCode]);

	const handleCheckoutClose = useCallback(async () => {
		setCheckoutModalOpened(false);
		await invalidateReferralAndPlan();
		ensureReferralCheckoutCooldownTimestampIfAbsent();
		setOpened(false);
	}, [invalidateReferralAndPlan]);

	return {
		opened,
		checkoutModalOpened,
		isRedeeming,
		proMonthly,
		rewardCode,
		referrerName: referredByEmail || "a friend",
		handleClose,
		handleClaimReward,
		handleCheckoutClose,
	};
}
